import { useState, useEffect, useRef, useCallback } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { createPortal } from "react-dom"
import { FaSearch, FaTimes } from "react-icons/fa"

const SuggestionsPortal = ({ children }) => {
  const [portalContainer] = useState(() => document.createElement("div"))

  useEffect(() => {
    document.body.appendChild(portalContainer)
    return () => {
      document.body.removeChild(portalContainer)
    }
  }, [portalContainer])

  return createPortal(children, portalContainer)
}

export default function HeroSection({
  searchTerm,
  setSearchTerm,
  suggestions,
  isTyping,
  setIsTyping,
  isLoading,
  error,
  selectedSuggestionIndex,
  setSelectedSuggestionIndex,
  handleSuggestionClick,
  handleKeyDown,
  handleViewMore,
}) {
  const inputRef = useRef(null)
  const suggestionsRef = useRef(null)

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value)
    setIsTyping(true)
    setSelectedSuggestionIndex(-1)
  }

  const handleClickOutside = useCallback(
    (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setIsTyping(false)
      }
    },
    [setIsTyping],
  )

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [handleClickOutside])

  return (
    <section className="relative overflow-hidden mb-16 md:mb-32">
      <motion.div
        className="absolute inset-0 z-0"
        animate={{
          scale: [1, 1.1, 1.2, 1.1, 1],
          rotate: [0, 90, 180, 270, 360],
        }}
        transition={{
          duration: 20,
          ease: "linear",
          repeat: Number.POSITIVE_INFINITY,
        }}
      >
        <div className="w-full h-full bg-gradient-to-br from-blue-500 to-purple-600 opacity-30 rounded-full blur-3xl"></div>
      </motion.div>
      <div className="relative z-10 flex flex-col items-center justify-center py-10 md:py-20 text-center">
        <motion.h1
          className="text-4xl md:text-5xl lg:text-7xl font-bold mb-4 md:mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          Welcome to The Library Hub
        </motion.h1>
        <motion.p
          className="text-md mx-4 md:text-xl lg:text-2xl text-gray-300 mb-6 md:mb-10 max-w-2xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          Discover thousands of libraries and start your learning journey today!
        </motion.p>
        <motion.div
          className="w-full max-w-md relative"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <div className="relative flex items-center px-4 md:px-8">
            <input
              ref={inputRef}
              type="text"
              placeholder="Search for libraries..."
              className="w-full px-4 pr-10 md:pr-12 py-3 rounded-full bg-white bg-opacity-20 backdrop-blur-md text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-300 text-sm md:text-base"
              value={searchTerm}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={() => setIsTyping(true)}
            />
            <FaSearch className="absolute right-8 md:right-12 text-gray-300 pointer-events-none" />
          </div>

          <AnimatePresence>
            {isTyping && (suggestions.length > 0 || error) && (
              <SuggestionsPortal>
                <motion.div
                  ref={suggestionsRef}
                  className="absolute z-50 w-full max-w-md mt-2 bg-white bg-opacity-95 backdrop-blur-md rounded-lg shadow-lg"
                  style={{
                    top: inputRef.current ? inputRef.current.getBoundingClientRect().bottom + window.scrollY : 0,
                    left: inputRef.current ? inputRef.current.getBoundingClientRect().left : 0,
                  }}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  <div className="flex justify-between items-center px-4 py-2 bg-gray-100">
                    <span className="font-semibold text-gray-700">Suggestions</span>
                    <button onClick={() => setIsTyping(false)} className="text-gray-500 hover:text-gray-700">
                      <FaTimes />
                    </button>
                  </div>
                  {isLoading ? (
                    <p className="px-4 py-2 text-gray-600">Loading...</p>
                  ) : error ? (
                    <p className="px-4 py-2 text-red-500">{error}</p>
                  ) : (
                    <>
                      <ul className="divide-y divide-gray-200">
                        {suggestions.map((library, index) => (
                          <motion.li
                            key={library.id}
                            className={`px-4 py-3 hover:bg-blue-50 cursor-pointer transition-colors duration-200 ${
                              index === selectedSuggestionIndex ? "bg-blue-100" : ""
                            }`}
                            onClick={() => handleSuggestionClick(library)}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            <p className="font-semibold text-gray-800">{library.name}</p>
                            <p className="text-sm text-gray-600">{library.address}</p>
                          </motion.li>
                        ))}
                      </ul>
                      <div className="px-4 py-2 bg-gray-100">
                        <button
                          onClick={handleViewMore}
                          className="w-full text-center text-blue-600 hover:text-blue-800 font-semibold"
                        >
                          View More
                        </button>
                      </div>
                    </>
                  )}
                </motion.div>
              </SuggestionsPortal>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  )
}
